const baseTheme = {
    version: 3,
    mode: 'adaptive',
    sideNavWidth: 260,
    scrimColor: 'rgba(0, 0, 0, 0.55)',
    fonts: {
        heading: 'Geist_medium',
        light: 'Geist_light',
        body: 'Geist_regular',
        bold: 'Geist_semibold'
    },
    roundness: {
        sm: 8,
        md: 12,
        lg: 16,
        xl: 20,
        xxl: 24,
    },
    spacing: {
        xs: 4,
        sm: 8,
        md: 16,
        lg: 24,
        xl: 32,
        xxl: 48,
    },
    typography: {
        hero: 28,
        h1: 22,
        h2: 18,
        h3: 16,
        body: 15,
        caption: 13,
        micro: 11,
    },
    animation: {
        fast: 150,
        normal: 250,
        slow: 400,
        spring: { damping: 18, stiffness: 180 },
    },
    card: {
        borderRadius: 14,
        padding: 16,
    },
}

const darkTheme = {
    ...baseTheme,
    dark: true,
    scheme: 'dark',
    colors: {
        background: "#15171A",
        backgroundTransparent: "#15171A99",
        onBackground: "#E5E7EB",

        surface: "#1E2125",
        surfaceElevated: "#2D3137",
        onSurface: "#E5E7EB",
        surfaceVariant: "#272A2F",
        inputBackground: "#272A2F",
        onSurfaceVariant: "#B9BEC7",

        border: "#383D45",
        borderSubtle: "#2B2F35",
        hoverCard: "#24272C",
        hover: "#2A2E34",
        hoverStrong: "#FFFFFF14",
        outline: "#767E89",

        success: "#34D399",
        successContainer: "#143A2B",
        bgSuccess: "#0B2A1A",
        onSuccess: "#0B2A1A",
        successSubtle: "#34D3991F",

        warning: "#FBBF24",
        warningContainer: "#2A2008",
        bgWarning: "#2A2008",
        onWarning: "#2A2008",
        warningSubtle: "#FBBF241F",

        info: "#7BA8E6",
        infoContainer: "#0E1A2E",
        bgInfo: "#0E1A2E",
        onInfo: "#0E1A2E",
        infoSubtle: "#7BA8E61F",

        text: "#F2F3F5",
        textSecondary: "#838790",
        textSecondaryContainer: "#A8ADB733",
        textTertiary: "#818892",
        onTextSecondary: "#24272C",
        onText: "#24272C",

        primary: "#7B9BFF",
        primaryMuted: "#5A7AE6",
        bgPrimary: "#7B9BFF1A",
        onPrimary: "#0A1A3D",
        primaryContainer: "#7B9BFF26",

        secondary: "#4DD9C0",
        secondaryMuted: "#2EB8A0",
        onSecondary: "#003D36",
        secondaryContainer: "#4DD9C01F",

        tertiary: "#C4A8FF",
        tertiaryMuted: "#A07EE6",
        onTertiary: "#2D1260",
        tertiaryContainer: "#C4A8FF1F",

        error: "#FF8A80",
        errorMuted: "#E65C50",
        bgError: "#bf2d226c",
        onError: "#3D0A07",
        errorContainer: "#FF8A801F",
        onErrorContainer: "#FFB4AB",

        accent: "#F0B866",

        // Milestones module accent — readable gold in both schemes.
        gold: "#EAB308",
        onGold: "#422006",
        goldContainer: "#3A2F0B",

        // --- Module Custom Colors (Dark Mode) ---
        people: "#818CF8", // Indigo (separates from core primary blue)
        onPeople: "#121843",
        peopleContainer: "#1E254A",

        events: "#FB923C", // Energetic Orange
        onEvents: "#431407",
        eventsContainer: "#422A1B", // Lifted warm brown — readable over #1E2125 surface

        tasks: "#FBBF24", // Service Amber
        onTasks: "#422006",
        tasksContainer: "#3A2F0B",

        groups: "#E879F9", // Community Fuchsia
        onGroups: "#4A044E",
        groupsContainer: "#2A1230",

        training: "#A3E635", // Growth Lime (distinct from success mint green)
        onTraining: "#1A3007",
        trainingContainer: "#1A2410",

        paths: "#FB7185", // Workflow Coral
        onPaths: "#4C0519",
        pathsContainer: "#42222b",

        giving: "#4ADE80", // Generosity Emerald
        onGiving: "#064E3B",
        givingContainer: "#1A4433",
        // Darker hero gradient stops for dark mode (the bright giving/success
        // tokens above are too high-contrast as a large gradient surface)
        givingHeroStart: "#065F46",
        givingHeroEnd: "#14532D",
        onGivingHero: "#D1FAE5",

        analytics: "#94A3B8", // Cool Data Slate
        onAnalytics: "#0F172A",
        analyticsContainer: "#1E293B",

        admin: "#9CA3AF", // Control Panel Neutral Gray
        onAdmin: "#111827",
        adminContainer: "#1F2937",

        connect: "#38BDF8", // Connect Cyan
        onConnect: "#0C4A6E",
        connectContainer: "#0F2D4A",

        forms: "#2DD4BF", // Forms Teal
        onForms: "#042F2E",
        formsContainer: "#0D3B38",

        chartColors: {
            blue: "#7BA8E6",
            green: "#4DD9C0",
            yellow: "#FBBF24",
            orange: "#F09B59",
            purple: "#C4A8FF",
        },
        // React Navigation theme compatibility
        card: "#1E2125",
        notification: "#7B9BFF",
    },
    shadows: {
        none: "none",
        card: "0px 2px 10px -2px #00000030",
        cardHover: "0px 4px 20px -2px #00000059",
        elevated: "0px 8px 30px -4px #00000066",
        header: "0px 1px 3px 0px #0000004D",
        inner: "inset 0px 1px 2px #00000033",
        input: "inset 0px 0px 0px 1px #FFFFFF0F, inset 0px 1px 2px #00000040",
    },
}

const lightTheme = {
    ...baseTheme,
    dark: false,
    scheme: 'light',
    colors: {
        background: "#F2F2EF",
        backgroundTransparent: "#F2F2EF99",
        onBackground: "#24262A",

        surface: "#FFFFFF",
        surfaceElevated: "#FAFAF8",
        onSurface: "#24262A",
        surfaceVariant: "#E9E9E5",
        inputBackground: "#EDEDEB",
        onSurfaceVariant: "#45494F",

        border: "#e5e5e2",
        borderSubtle: "#e5e5e2",
        hoverCard: "#F4F4F1",
        hover: "#f6f6f5",
        hoverStrong: "#00000012",
        outline: "#7A8088",

        success: "#16A34A",
        successContainer: "#E8F5EC",
        bgSuccess: "#c8e9d2ff",
        onSuccess: "#E8F5EC",
        successSubtle: "#16A34A14",

        warning: "#E5A00D",
        warningContainer: "#FFF7E6",
        bgWarning: "#ecdebfff",
        onWarning: "#3D2E05",
        warningSubtle: "#E5A00D14",

        info: "#4B82D4",
        infoContainer: "#EBF1FB",
        bgInfo: "#c4d5f0ff",
        onInfo: "#0E1A2E",
        infoSubtle: "#4B82D414",

        text: "#24262A",
        textSecondary: "#81868e",
        textSecondaryContainer: "#62676F33",
        textTertiary: "#878D96",
        onTextSecondary: "#FFFFFF",

        primary: "#4361EE",
        primaryMuted: "#6580F5",
        bgPrimary: "#4361EE12",
        onPrimary: "#FFFFFF",
        primaryContainer: "#4361EE1A",

        secondary: "#0D9488",
        secondaryMuted: "#2EB8A0",
        onSecondary: "#FFFFFF",
        secondaryContainer: "#0D948814",

        tertiary: "#7C3AED",
        tertiaryMuted: "#9B6BF5",
        onTertiary: "#FFFFFF",
        tertiaryContainer: "#7C3AED14",

        error: "#DC2626",
        errorMuted: "#EF4444",
        bgError: "#f3e0e0ff",
        onError: "#FFFFFF",
        errorContainer: "#FEF2F2",
        onErrorContainer: "#450A0A",

        accent: "#D97706",

        // Milestones module accent — readable gold in both schemes.
        gold: "#A16207",
        onGold: "#FFFFFF",
        goldContainer: "#FEF3C7",

        // --- Module Custom Colors (Light Mode) ---
        people: "#4F46E5", // Indigo
        onPeople: "#FFFFFF",
        peopleContainer: "#EEF2FF",

        events: "#E65F00", // Darkened Orange (for clear contrast/readability)
        onEvents: "#FFFFFF",
        eventsContainer: "#FED7AA", // Orange-200 tint — readable over #FAFAF8 surface

        tasks: "#92400E", // Service Amber
        onTasks: "#FFFFFF",
        tasksContainer: "#FDE68A",

        groups: "#C026D3", // Fuchsia
        onGroups: "#FFFFFF",
        groupsContainer: "#FCE7F3",

        training: "#65A30D", // Lime
        onTraining: "#FFFFFF",
        trainingContainer: "#F7FEE7",

        paths: "#E11D48", // Workflow Coral
        onPaths: "#FFFFFF",
        pathsContainer: "#FFE4E6",

        giving: "#059669", // Emerald
        onGiving: "#FFFFFF",
        givingContainer: "#E6F4EA",
        givingHeroStart: "#059669",
        givingHeroEnd: "#16A34A",
        onGivingHero: "#FFFFFF",

        analytics: "#334155", // Slate Blue
        onAnalytics: "#FFFFFF",
        analyticsContainer: "#F1F5F9",

        admin: "#4B5563", // Muted Charcoal
        onAdmin: "#FFFFFF",
        adminContainer: "#F3F4F6",

        connect: "#0369A1", // Connect Cyan
        onConnect: "#FFFFFF",
        connectContainer: "#E0F2FE",

        forms: "#0D9488", // Forms Teal
        onForms: "#FFFFFF",
        formsContainer: "#CCFBF1",

        chartColors: {
            blue: "#4B82D4",
            green: "#16A34A",
            yellow: "#E5A00D",
            orange: "#EA7B30",
            purple: "#7C3AED",
        },
        // React Navigation theme compatibility
        card: "#FAFAF8",
        notification: "#4361EE",
    },
    shadows: {
        none: "none",
        card: "0px 0px 1px #00000014, 0px 0px 8px #00000012",
        cardHover: "0px 4px 16px #00000014, 0px 2px 6px #0000000A",
        elevated: "0px 8px 24px #00000014, 0px 2px 8px #0000000A",
        header: "0px 1px 3px #0000000F",
        inner: "inset 0px 1px 2px #0000000F",
        input: "inset 0px 0px 0px 1px #0000000A, inset 0px 1px 2px #0000000F",
    },
}

export { lightTheme, darkTheme }